import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";

// Simple React Modal using portals
const Modal = ({ open, onClose, onConfirm, children }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-sm w-full p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
        <div className="flex justify-end gap-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded text-gray-700 border hover:bg-gray-50 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white font-bold"
          >
            Yes, Buy!
          </button>
        </div>
      </div>
    </div>
  );
};

const Topup = () => {
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processingPlanId, setProcessingPlanId] = useState(null);
  const [message, setMessage] = useState(null);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const apiUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(null);
        const vendor_id = localStorage.getItem("vendorid");
        const res = await fetch(
          `${apiUrl}ESIM/Get_topup_plans?flag=2&vendorid=${vendor_id}`,
          {
            method: "GET",
            headers: {
              authorization: `Bearer ${localStorage.getItem("token") || ""}`,
              accept: "*/*",
            },
          }
        );

        if (!res.ok) {
          throw new Error(`HTTP error: ${res.status}`);
        }

        const data = await res.json();

        if (!data.isSuccess) {
          throw new Error(data.message || "Failed to fetch plans");
        }

        setPlans(data.getpartner_plan || []);
        setFilteredPlans(data.getpartner_plan || []);
      } catch (err) {
        const message = err?.message || "Something went wrong";
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  useEffect(() => {
    if (search.trim() === "") {
      setFilteredPlans(plans);
    } else {
      setFilteredPlans(
        plans.filter(
          (plan) =>
            plan.planename.toLowerCase().includes(search.toLowerCase())
            || plan.countryname.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  }, [search, plans]);

  const formatDataAllowance = (plan) => {
    const type = (plan.dataallowancetype || "").toLowerCase();
    if (!plan.dataallowance || type === "null" || type === "undefined") {
      return "Unlimited";
    }
    return `${plan.dataallowance} ${plan.dataallowancetype}`;
  };

  // helper: simple UUID v4 generator (fallback if no guid in localStorage)
  const generateUUID = () =>
    "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });

  const handleBuyClick = (plan) => {
    setSelectedPlan(plan);
    setShowModal(true);
  };

  const handleConfirmBuy = async () => {
    setShowModal(false);
    setMessage(null);

    if (!selectedPlan) return;

    const planid = selectedPlan.p_id;
    const partnercode = localStorage.getItem("partnerCode") ?? "";
    const iccid = localStorage.getItem("Iccid") ?? "";
    const guid = localStorage.getItem("guid") ?? generateUUID();
    const cip = localStorage.getItem("cip") ?? "";

    if (!iccid) {
      toast.error("ICCID not found in localStorage.");
      return;
    }

    setProcessingPlanId(planid);

    try {
      const res = await fetch(`${apiUrl}ESIM/purchaseTopup_plan`, {
        method: "POST",
        headers: {
          Accept: "*/*",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          planid,
          partnercode,
          iccid,
          guid,
          cip,
        }),
      });

      if (!res.ok) {
        let text = `HTTP error: ${res.status}`;
        try {
          const errJson = await res.json();
          if (errJson?.message) text = `${errJson.message} (status ${res.status})`;
        } catch {
          // ignore parse error
        }
        throw new Error(text);
      }

      const data = await res.json();

      if (data.isSuccess === false) {
        throw new Error(data.message || "Purchase failed");
      }

      setMessage({ type: "success", text: data.message || "Purchase successful" });
    } catch (err) {
      setMessage({ type: "error", text: err?.message || "Purchase failed" });
    } finally {
      setProcessingPlanId(null);
      setSelectedPlan(null);
    }
  };

  return (
    <div className="overflow-y-auto flex-1 px-2 sm:px-10 py-6">
      <h1 className="text-3xl font-bold mb-8 text-gray-900">Top-up Plans</h1>

      <div className="mb-5 flex justify-end">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by plan or country"
          className="border border-gray-300 rounded-md px-4 py-2 shadow focus:ring-2 focus:ring-green-400"
        />
      </div>

      {loading && (
        <div className="flex justify-center items-center h-32">
          <p className="text-gray-600 text-lg animate-pulse">Loading plans, please wait…</p>
        </div>
      )}

      {error && (
        <div className="flex justify-center items-center h-32">
          <p className="text-red-600 text-lg">Failed to load plans: {error}</p>
        </div>
      )}

      {message && (
        <div
          className={`mb-4 mx-auto max-w-lg p-3 rounded shadow transition-all ${
            message.type === "success"
              ? "bg-green-100 text-green-800 border border-green-300"
              : "bg-red-100 text-red-800 border border-red-300"
          }`}
        >
          {message.text}
        </div>
      )}

      {!loading && !error && filteredPlans.length === 0 && (
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-600 text-xl font-semibold text-center">
No Top-up Available.          </p>
        </div>
      )}

      {!loading && !error && filteredPlans.length > 0 && (
        <div className="overflow-x-auto border border-gray-300 rounded-xl shadow-lg">
          <table className="min-w-full divide-y divide-gray-200 text-base">
            <thead className="bg-gray-100 sticky top-0">
              <tr>
                <th className="border px-4 py-3 text-center font-semibold">Plan ID</th>
                <th className="border px-4 py-3 text-center font-semibold">Plan Name</th>
                <th className="border px-4 py-3 text-center font-semibold">Country</th>
                <th className="border px-4 py-3 text-center font-semibold">Validity</th>
                <th className="border px-4 py-3 text-center font-semibold">Data Allowance</th>
                <th className="border px-4 py-3 text-center font-semibold">Price (USD)</th>
                <th className="border px-4 py-3 text-center font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {filteredPlans.map((plan) => {
                const isProcessing = processingPlanId === plan.p_id;
                return (
                  <tr key={plan.p_id} className="hover:bg-gray-50 transition-colors">
                    <td className="border px-4 py-3 text-center">{plan.p_id}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">{plan.planename}</td>
                    <td className="border px-4 py-3 text-center">{plan.countryname}</td>
                    <td className="border px-4 py-3 text-center">
                      {plan.validity} {plan.validitytype}
                    </td>
                    <td className="border px-4 py-3 text-center">{formatDataAllowance(plan)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-900">${plan.sellingcost}</td>
                    <td className="border px-4 py-3 text-center">
                      <button
                        onClick={() => handleBuyClick(plan)}
                        disabled={isProcessing}
                        className="font-bold shadow-md rounded-xl bg-green-600 hover:bg-green-700 text-white px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                      >
                        {isProcessing ? "Processing…" : "Buy"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirmBuy}
      >
        <div className="mb-3">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Confirm Purchase</h2>
          <p className="text-gray-700">
            Are you sure you want to purchase{" "}
            <span className="font-bold text-green-800">
              {selectedPlan?.planename}
            </span>{" "}
            for <span className="font-bold text-green-800">${selectedPlan?.sellingcost}</span>?
          </p>
          {selectedPlan?.countryname && (
            <p className="text-sm mt-1 text-gray-500">
              Country: <span className="text-gray-800">{selectedPlan.countryname}</span>
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default Topup;
